// Long-form SEO guide articles (content marketing for long-tail local search).

export const GUIDE_LINKS = [
  { slug: "house-cleaning-cost-hamilton", title: "What does house cleaning cost in Hamilton?", path: "/guides/house-cleaning-cost-hamilton" },
  { slug: "move-out-cleaning-checklist-hamilton", title: "Move-out cleaning checklist for Hamilton renters", path: "/guides/move-out-cleaning-checklist-hamilton" },
  { slug: "deep-clean-vs-regular-clean", title: "Deep clean vs. regular clean: what's the difference?", path: "/guides/deep-clean-vs-regular-clean" },
];

export const ARTICLES = [
  {
    slug: "move-out-cleaning-checklist-hamilton",
    title: "Move-Out Cleaning Checklist for Hamilton Renters (2026)",
    metaTitle: "Move-Out Cleaning Checklist for Hamilton Renters (2026)",
    description:
      "A room-by-room move-out cleaning checklist to help Hamilton renters leave a place spotless and protect their deposit — plus when it's worth booking a professional move-out clean.",
    image: "/life-family.jpg",
    overline: "Guide · Renters",
    heading: "Your move-out cleaning checklist (Hamilton renters)",
    lead: "Moving is stressful enough. Here's a simple, room-by-room checklist to help you hand back a spotless place, keep your landlord happy, and protect your deposit.",
    sections: [
      {
        heading: "Why a good move-out clean matters",
        paras: [
          "When you're moving out of a rental in Hamilton, the state you leave it in can directly affect how much of your deposit comes back. A genuinely clean home also makes the final walkthrough smoother and leaves things on good terms with your landlord or property manager.",
          "The goal is simple: leave the place looking as fresh as the day you moved in. Here's how to work through it without missing the spots landlords tend to check.",
        ],
      },
      {
        heading: "Kitchen",
        paras: ["The kitchen gets the closest inspection, so give it extra attention:"],
        list: [
          "Clean inside and out of the oven, stovetop and range hood",
          "Wipe inside the fridge and freezer (defrost if needed)",
          "Empty and wipe inside all cupboards and drawers",
          "Degrease backsplash, counters and cabinet fronts",
          "Scrub the sink and polish the faucet",
          "Sweep and mop the floor, including under appliances if you can move them safely",
        ],
      },
      {
        heading: "Bathrooms",
        paras: ["Landlords look hard at bathrooms — aim for sparkling:"],
        list: [
          "Descale and scrub the shower, tub and glass",
          "Clean and disinfect the toilet, inside and out",
          "Wipe down the vanity, sink and mirror",
          "Clear soap scum from tiles and grout",
          "Empty the medicine cabinet and wipe it out",
          "Wash the floor and take out any bins",
        ],
      },
      {
        heading: "Bedrooms & living areas",
        paras: ["These go quickly once the clutter's gone:"],
        list: [
          "Dust all surfaces, shelves, sills and baseboards",
          "Wipe light switches, door handles and vents",
          "Clean inside closets and wardrobes",
          "Spot-clean walls and remove any wall hooks or nails (fill holes if your lease requires)",
          "Vacuum carpets thoroughly; sweep and mop hard floors",
          "Clean interior windows and wipe frames",
        ],
      },
      {
        heading: "The spots people forget",
        paras: ["A few easy-to-miss areas that make a big difference at the walkthrough:"],
        list: [
          "Inside the dishwasher and washing machine drum",
          "Behind and beneath appliances",
          "Ceiling fans and light fixtures",
          "Baseboards, door tops and closet shelving",
          "Patio, balcony or entryway",
        ],
      },
      {
        heading: "A simple timeline",
        paras: [
          "Start with the deep stuff (oven, fridge, bathrooms) a day or two before you hand over the keys, ideally once the big furniture is out. Save floors for last, working your way toward the door so you're not walking back over clean surfaces.",
          "If your schedule is tight — or you'd simply rather not spend your last day scrubbing — a professional move-out clean is the easy button. It's one of the most common reasons Hamilton renters call us.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does a professional move-out clean help me get my deposit back?",
        a: "It certainly helps. A thorough, professional clean addresses the exact areas landlords inspect — ovens, bathrooms, floors and inside cabinets — so the place shows beautifully at the final walkthrough. We can't guarantee a landlord's decision, but a spotless home removes cleaning as a reason to withhold your deposit.",
      },
      {
        q: "What does a move-out clean include with Wisp?",
        a: "Our move in/out cleaning is a detailed, whole-home clean including inside appliances, cupboards and cabinets, bathrooms, floors and all the easy-to-miss spots. We confirm exactly what your place needs during a free in-home visit, so the price and scope are clear up front.",
      },
      {
        q: "How far in advance should I book?",
        a: "As early as you can — moving season fills up fast. A quick call or text to 289-799-2690 lets us reserve a time that lines up with your move-out date.",
      },
    ],
  },
  {
    slug: "deep-clean-vs-regular-clean",
    title: "Deep Clean vs. Regular Clean: What's the Difference?",
    metaTitle: "Deep Clean vs. Regular Clean: What's the Difference?",
    description:
      "Not sure whether you need a deep clean or a regular maintenance clean? Here's a simple, honest breakdown to help Hamilton-area homeowners choose the right one.",
    image: "/svc-residential.jpg",
    overline: "Guide · Choosing a clean",
    heading: "Deep clean vs. regular clean — which do you need?",
    lead: "It's one of the most common questions we hear. Here's a plain-language look at the difference, so you can choose what's right for your home (and your budget).",
    sections: [
      {
        heading: "What's a regular (maintenance) clean?",
        paras: [
          "A regular clean keeps an already-tidy home fresh and comfortable. It's the everyday upkeep most people picture — done on a weekly, bi-weekly or monthly rhythm.",
        ],
        list: [
          "Dusting surfaces, sills and reachable fixtures",
          "Vacuuming and mopping floors",
          "Wiping kitchen counters and exterior of appliances",
          "Cleaning and sanitizing bathrooms (toilet, sink, shower, mirror)",
          "Tidying and general freshening throughout",
        ],
      },
      {
        heading: "What's a deep clean?",
        paras: [
          "A deep clean is a top-to-bottom reset that reaches the areas a regular clean doesn't get to every visit. It takes more time and care — usually about 1.5 to 2 times a standard clean — but the result is a home that feels brand new.",
        ],
        list: [
          "Everything in a regular clean, plus:",
          "Inside the oven, fridge and cabinets",
          "Detailed scrubbing of tile, grout and soap scum",
          "Baseboards, door frames, vents and light fixtures",
          "Behind and beneath movable furniture and appliances",
          "Fine detail work throughout, corner to corner",
        ],
      },
      {
        heading: "How to know which one you need",
        paras: [
          "If your home is generally kept up and you just want to stay on top of it, a regular clean is perfect. If it's been a while, you're moving in or out, prepping for guests or a special occasion, or you simply want a fresh start — a deep clean is the way to go.",
        ],
      },
      {
        heading: "The rhythm most homes settle into",
        paras: [
          "Many of our clients begin with a one-time deep clean to bring everything up to a comfortable baseline, then switch to a lighter, more affordable recurring clean to keep it there. It's the easiest, most budget-friendly way to keep a home consistently lovely.",
          "Not sure which fits your home? That's exactly what the free in-home visit is for — we'll take a look and give you an honest recommendation, with no pressure either way.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a deep clean more expensive than a regular clean?",
        a: "Yes — a deep clean covers much more detail work, so it typically costs about 1.5 to 2 times a standard maintenance clean. Many homes start with a deep clean, then move to a lighter, more affordable recurring rhythm.",
      },
      {
        q: "Do I need a deep clean before starting recurring service?",
        a: "Often it helps, especially if it's been a while since the last thorough clean. Starting from a fresh baseline makes every recurring visit faster and more effective. We'll let you know honestly whether it's worth it during your free visit.",
      },
      {
        q: "How often should I book a deep clean?",
        a: "If you're on a recurring schedule, many homes only need a deep clean once or twice a year (often seasonally). If you're not on a regular plan, a deep clean every few months keeps things comfortable.",
      },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
